import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import { DynamicForm } from "../../dynamicForm";
import type { DpiaById } from "../../api/dpia.types";
import type { AutocompleteOptionType } from "../../shared/Autocomplete";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  createFilterAutocompleteOption,
} from "../../shared/Autocomplete";
import { PersonIcon3 } from "../../shared/icons";
import { useUserInfo } from "../../api/userInfo";
import { useGetResponsibleUsers } from "../../api/users";
import { DpiaResponsibleWarningDialog } from "./DpiaResponsibleWarningDialog";

type DpiaResponsibleProps = { dpia: DpiaById; isReadOnly: boolean };

export const DpiaResponsibleSelect = ({ dpia, isReadOnly }: DpiaResponsibleProps) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const { inputState, setIsEditing } = DynamicForm.useInlineInputState(isReadOnly);
  const [selectedResponsible, setSelectedResponsible] = useState<AutocompleteOptionType>();
  const [wasHovered, setWasHovered] = useState(false);

  const responsible: AutocompleteOptionType = {
    label: `${dpia.responsible.firstName} ${dpia.responsible.lastName}`,
    value: dpia.responsible.userId,
    description: dpia.responsible.email,
  };

  const shouldQuery =
    inputState !== "readonly" && userInfo.email === responsible.description && (inputState === "editing" || wasHovered);

  const { data } = useGetResponsibleUsers(dpia.connectedTenant.tenantId, { enabled: shouldQuery });

  const options = data?.users.map(user => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.userId,
    description: user.email,
  }));

  return (
    <>
      <Autocomplete
        id="dpia-select-responsible"
        noOptionsText={t("DynamicForm.PersonSelect.Empty")}
        forcePopupIcon
        blurOnSelect
        onMouseOver={() => setWasHovered(true)}
        onBlur={() => setIsEditing(false)}
        onFocus={() => setIsEditing(true)}
        value={responsible}
        disableClearable
        disabled={isReadOnly || userInfo.email !== responsible.description}
        options={options ?? [AUTOCOMPLETE_LOADING_OPTION]}
        onChange={(_, option) => setSelectedResponsible(option)}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return <AutocompleteOption key={option.value} dense option={option} {...optionProps} />;
        }}
        clearOnBlur
        getOptionDisabled={option => option.value === responsible.value}
        isOptionEqualToValue={(option, selected) =>
          option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selected.value
        }
        sx={{ flexGrow: 1 }}
        data-testid="dpia-select-responsible"
        filterOptions={createFilterAutocompleteOption}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                startAdornment: <PersonIcon3 size={16} color={theme => theme.palette.text.primary} />,
                sx: DynamicForm.inlineInputStateStyles[inputState],
              },
            }}
          />
        )}
      />
      {selectedResponsible && (
        <DpiaResponsibleWarningDialog
          dpia={{ id: dpia.id, name: dpia.name }}
          responsible={{ id: selectedResponsible.value, name: selectedResponsible.label }}
          onClose={() => setSelectedResponsible(undefined)}
        />
      )}
    </>
  );
};

export const DpiaResponsible = ({ dpia, isReadOnly }: DpiaResponsibleProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h6">{t("DpiaDrawer.Responsible.Label")}</Typography>
      <Box sx={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
        <DpiaResponsibleSelect dpia={dpia} isReadOnly={isReadOnly} />
      </Box>
    </>
  );
};
